/**
 * Fix run-on markdown so headers/bullets render on their own lines.
 *
 * Streamed model output often glues a header or list item to the end of the prior
 * sentence (e.g. "…Outlook### Amazon", "…paid time off.- Amazon"), so react-markdown
 * shows it inline as one paragraph. It also likes to emit "pretty" Unicode bullets
 * (•, ▪, ‣, ·) which are NOT valid GFM list markers, so they render as literal text
 * and break the surrounding list. And because the text arrives in chunks, a marker
 * can get orphaned on its own line, split from the text that follows it.
 *
 * This conservatively repairs those cases without touching hyphenated words
 * ("co-founder"), numeric ranges ("2020-2026"), or real "## headings". Mirrors the
 * backend `normalize_markdown` (tools/hooks.py). Idempotent.
 */
export function normalizeMarkdown(text: string): string {
  if (!text) return text;
  let t = text;

  // Normalise CRLF so the line-anchored rules below behave consistently.
  t = t.replace(/\r\n?/g, "\n");

  // 00) Em dash (U+2014) and en dash (U+2013) are forbidden anywhere in output -
  //     always a plain hyphen "-". Safety net mirroring the backend: a clause-break
  //     dash with a space on either side collapses to a spaced " - "; a glued dash
  //     (range/compound) becomes a bare "-". Runs first so later rules see a hyphen.
  t = t.replace(/[ \t]*[\u2014\u2013][ \t]*/g, (m) => (/[ \t]/.test(m) ? " - " : "-"));

  // 0) Orphaned reference-style citations: the model emits "[1]: Some Title" (a title
  //    where a URL should be), which the renderer eats into a broken "- : title".
  //    Turn "[label]: <non-URL text>" lines into plain bullets. Real link-defs
  //    ("[1]: https://…") are left untouched.
  t = t.replace(/^[ \t]*\[[^\]]*\]:[ \t]+(?!https?:\/\/)(\S.*)$/gm, "- $1");
  // 0b) A list item left with an empty citation label ("- : Title") → "- Title".
  t = t.replace(/^([ \t]*[-*•▪‣·])[ \t]*:[ \t]+(?=\S)/gm, "$1 ");

  // 1) ATX header glued to preceding text → own line ("Outlook### Amazon").
  //    Preceding char must not be '#' (so a real "## Heading" isn't split apart).
  t = t.replace(/([^\n#])(#{1,6}\s)/g, "$1\n\n$2");
  // 2) Header marker with no space before its text ("###Amazon" → "### Amazon").
  //    Lookahead excludes '#' so a full "###" run isn't partially matched/corrupted.
  t = t.replace(/(^|\n)(#{1,6})(?=[^#\s])/g, "$1$2 ");

  // 3) Unicode "pretty" bullets are not GFM markers - convert a leading •/▪/‣/·
  //    (optionally indented) into a real "- " so the list renders. Preserves the
  //    indentation so nested lists keep their depth.
  t = t.replace(/^([ \t]*)[•▪‣·][ \t]*(?=\S)/gm, "$1- ");
  // 3b) An inline Unicode bullet glued to the end of a sentence ("…off.• Amazon")
  //     → break it onto its own real list item. Same guard as the dash rule below
  //     (needs sentence punctuation before it) so mid-sentence "·" isn't split.
  t = t.replace(/([.!?:;)\]"’])[ \t]*[•▪‣·][ \t]*(?=[A-Z0-9"“‘(])/g, "$1\n\n- ");

  // 4) Inline dash-bullet after sentence-ending punctuation → real list item (the
  //    dash may be glued with no space). Never splits "co-founder"/"2020-2026".
  t = t.replace(/([.!?:;)\]"’])\s*-\s+(?=[A-Z0-9"“‘(])/g, "$1\n\n- ");

  // 5) Re-join a list/heading marker that got orphaned on its own line, split from
  //    the text that follows it (a streaming chunk-boundary artefact). e.g.
  //      "-\nAmazon"      → "- Amazon"
  //      "1.\nRevenue"    → "1. Revenue"
  //      "###\nOutlook"   → "### Outlook"
  //    Only fires when the NEXT line has text (not another marker / blank line), so
  //    a deliberately empty list item or a real blank line is untouched.
  t = t.replace(/^([ \t]*(?:[-*]|\d+\.|#{1,6}))[ \t]*\n(?![ \t]*(?:[-*]|\d+\.|#{1,6})[ \t]|[ \t]*$)[ \t]*(\S)/gm, "$1 $2");

  // 6) Collapse 3+ blank lines.
  t = t.replace(/\n{3,}/g, "\n\n");
  return t;
}
